import React from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router"

import { AboutDialog } from "@/components/about-dialog"
import { ContactDialog } from "@/components/contact-dialog"
import { If } from "@/components/if/if"
import { RoleSelectionDialog } from "@/components/role-selection-dialog"
import { UserSelectionDialog } from "@/components/user-selection-dialog"
import { useAuthentication } from "@/contexts"
import { useFormatPersonName } from "@/formatters"
import { RootPath } from "@/models/route"
import type { User } from "@/models/user"
import type { UserRoleType } from "@/models/user-role"
import { HStack } from "@/nzds/layout"
import { useDisclosure } from "@/nzds/utils"
import { singleLineEllipsisStyles } from "@/styles/utils"
import { useUrl } from "@/utils/url/use-url"

import {
  Avatar,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material"

export const UserMenu: React.FC = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { isAdminUser } = useAuthentication()
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null
  )
  const isMenuOpen = Boolean(menuAnchorEl)
  const {
    activeUser,
    authenticatedUser,
    setActiveUser,
    setActiveRole,
    setIsImpersonatingRole,
    signOut,
  } = useAuthentication()

  const { formatPersonName, formatPersonNameInitials } = useFormatPersonName()

  const { userSettingsPath } = useUrl()

  const {
    close: aboutDialogClose,
    isOpen: aboutDialogIsOpen,
    open: aboutDialogOpen,
  } = useDisclosure()

  const {
    close: contactDialogClose,
    isOpen: contactDialogIsOpen,
    open: contactDialogOpen,
  } = useDisclosure()

  const {
    close: impersonateUserDialogClose,
    isOpen: impersonateUserDialogIsOpen,
    open: impersonateUserDialogOpen,
  } = useDisclosure()

  const {
    close: impersonateRoleDialogClose,
    isOpen: impersonateRoleDialogIsOpen,
    open: impersonateRoleDialogOpen,
  } = useDisclosure()

  const closeMenu = (): void => {
    setMenuAnchorEl(null)
  }

  const openMenu = (element: HTMLElement): void => {
    setMenuAnchorEl(element)
  }

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>): void => {
    openMenu(event.currentTarget)
  }

  const handleImpersonateUserDialogConfirm = (user: User): void => {
    // When impersonating a user, reset any role impersonation
    setActiveRole(user.role)
    setIsImpersonatingRole(false)
    setActiveUser(user)
    impersonateUserDialogClose()
    closeMenu()
  }

  const handleImpersonateRoleDialogConfirm = (role: UserRoleType): void => {
    // When impersonating a role, clear any user impersonation
    setActiveUser(authenticatedUser)
    setActiveRole(role)
    setIsImpersonatingRole(true)
    impersonateRoleDialogClose()
    closeMenu()
  }

  const handleSignOutClick = () => {
    void signOut()
  }

  const userInitials = activeUser
    ? formatPersonNameInitials({
        firstName: activeUser.firstName,
        lastName: activeUser.lastName,
      })
    : ""
  const activeUserDisplayName = activeUser
    ? formatPersonName({
        firstName: activeUser.firstName,
        lastName: activeUser.lastName,
      })
    : ""

  return (
    <>
      <IconButton
        aria-controls={isMenuOpen ? "user-menu" : undefined}
        aria-expanded={isMenuOpen ? "true" : undefined}
        aria-haspopup="true"
        aria-label={`${activeUserDisplayName} (${userInitials})`}
        data-e2e="current-user-button"
        id="current-user-button"
        onClick={handleAvatarClick}
        sx={{ padding: 0 }}
      >
        <Avatar
          alt={activeUserDisplayName}
          sx={{
            bgcolor: theme.palette.spectrum.peach[300],
            fontSize: theme.typography.pxToRem(18),
          }}
        >
          {userInitials}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        id="user-menu"
        slotProps={{
          list: {
            "aria-labelledby": "current-user-button",
          },
          paper: {
            style: {
              maxHeight: "calc(100vh - 92px)",
              width: theme.typography.pxToRem(280),
            },
          },
        }}
        onClose={closeMenu}
        open={isMenuOpen}
        sx={{
          mt: 1,
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem>
          <HStack sx={{ alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.spectrum.peach[300],
                height: theme.typography.pxToRem(36),
                width: theme.typography.pxToRem(36),
              }}
            >
              {userInitials}
            </Avatar>
            <Typography
              sx={{
                ...singleLineEllipsisStyles,
              }}
            >
              {activeUserDisplayName}
            </Typography>
          </HStack>
        </MenuItem>

        <Divider />
        <If condition={isAdminUser}>
          <MenuItem onClick={impersonateRoleDialogOpen}>
            <Typography noWrap>
              {t("impersonateRole.title", { ns: "common" })}
            </Typography>
          </MenuItem>
          <MenuItem onClick={impersonateUserDialogOpen}>
            <Typography noWrap>
              {t("impersonateUser.title", { ns: "common" })}
            </Typography>
          </MenuItem>
        </If>

        <MenuItem
          component={RouterLink}
          onClick={closeMenu}
          to={userSettingsPath}
        >
          <Typography noWrap>
            {t("moduleTitle.userSettings", { ns: "common" })}
          </Typography>
        </MenuItem>

        <MenuItem onClick={contactDialogOpen}>
          <Typography noWrap>
            {t("contactSupport.title", { ns: "common" })}
          </Typography>
        </MenuItem>
        {isAdminUser && (
          <MenuItem
            component={Link}
            href={`/${RootPath.DeveloperAdmin}`}
            onClick={closeMenu}
          >
            <Typography noWrap>
              {t("userMenu.administrationSettings", { ns: "common" })}
            </Typography>
          </MenuItem>
        )}
        <MenuItem onClick={aboutDialogOpen}>
          <Typography noWrap>
            {t("userMenu.aboutApp", { ns: "common" })}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOutClick}>
          <Typography noWrap>
            {t("userMenu.signOut", { ns: "common" })}
          </Typography>
        </MenuItem>
      </Menu>

      <AboutDialog open={aboutDialogIsOpen} onClose={aboutDialogClose} />

      <ContactDialog
        open={contactDialogIsOpen}
        onClose={contactDialogClose}
        type="support"
      />

      <UserSelectionDialog
        confirmText={t("impersonateUser.confirmMessage", { ns: "common" })}
        open={impersonateUserDialogIsOpen}
        onClose={impersonateUserDialogClose}
        onConfirm={handleImpersonateUserDialogConfirm}
        title={t("impersonateUser.promptMessage", { ns: "common" })}
      />

      <RoleSelectionDialog
        confirmText={t("impersonateRole.confirmMessage", { ns: "common" })}
        open={impersonateRoleDialogIsOpen}
        onClose={impersonateRoleDialogClose}
        onConfirm={handleImpersonateRoleDialogConfirm}
        title={t("impersonateRole.promptMessage", { ns: "common" })}
      />
    </>
  )
}
